import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router';
import {
    Plus,
    Edit01,
    Trash01,
    SearchLg
} from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, DialogTrigger, Dialog, ModalOverlay } from '@/components/application/modals/modal';
import { documentSchemaService, documentAttributeService, DOCUMENT_TYPES } from '@/data/mock-document-schemas';
import type { DocumentAttribute, CreateDocumentAttributeData, AttributeDataType } from '@/types/document-schema';
import { useTranslation } from 'react-i18next';
import { X } from '@untitledui/icons';

export const DocumentTypeAttributesPage = () => {
    const { documentType } = useParams<{ documentType: string }>();
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const [attributes, setAttributes] = useState<DocumentAttribute[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [dataTypeFilter, setDataTypeFilter] = useState<AttributeDataType | ''>('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAttribute, setEditingAttribute] = useState<DocumentAttribute | null>(null);
    const [enumValues, setEnumValues] = useState<string[]>([]);
    const [enumValueInput, setEnumValueInput] = useState('');
    const [metadataJson, setMetadataJson] = useState('{}');
    const [metadataError, setMetadataError] = useState('');

    const currentDocType = DOCUMENT_TYPES.find((dt) => dt.key === documentType);

    const [formData, setFormData] = useState<CreateDocumentAttributeData>({
        attribute_key: '',
        label: '',
        description: '',
        data_type: 'string',
        validation_rules: {},
        enum_values: [],
        input_type: '',
        placeholder: '',
        help_text: '',
        category: '',
        display_order: 0,
        is_global: false, // Attributes per document type non sono globali
        attribute_metadata: {},
    });

    const dataTypeOptions = [
        { id: '', label: t('attributes.dataTypePlaceholder') },
        { id: 'string', label: t('attributes.dataTypes.string') },
        { id: 'number', label: t('attributes.dataTypes.number') },
        { id: 'date', label: t('attributes.dataTypes.date') },
        { id: 'datetime', label: t('attributes.dataTypes.datetime') },
        { id: 'boolean', label: t('attributes.dataTypes.boolean') },
        { id: 'array', label: t('attributes.dataTypes.array') },
        { id: 'object', label: t('attributes.dataTypes.object') },
        { id: 'email', label: t('attributes.dataTypes.email') },
        { id: 'url', label: t('attributes.dataTypes.url') },
        { id: 'phone', label: t('attributes.dataTypes.phone') },
    ];

    useEffect(() => {
        if (documentType) {
            loadAttributes();
        }
    }, [documentType, searchTerm, dataTypeFilter, categoryFilter]);

    const loadAttributes = () => {
        if (!documentType) return;
        setIsLoading(true);

        // Get attributes for this document type
        const allAttributes = documentSchemaService.getAttributesByDocumentType(documentType as any);

        // Apply filters
        let filtered = [...allAttributes];

        if (dataTypeFilter) {
            filtered = filtered.filter((a) => a.data_type === dataTypeFilter);
        }

        if (categoryFilter) {
            filtered = filtered.filter((a) => a.category === categoryFilter);
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (a) =>
                    a.attribute_key.toLowerCase().includes(term) ||
                    a.label.toLowerCase().includes(term) ||
                    a.description?.toLowerCase().includes(term)
            );
        }

        setAttributes(filtered);
        setIsLoading(false);
    };

    const categories = Array.from(new Set(attributes.map((a) => a.category).filter(Boolean)));

    const handleOpenModal = (attr?: DocumentAttribute) => {
        if (attr) {
            setEditingAttribute(attr);
            setFormData({
                attribute_key: attr.attribute_key,
                label: attr.label,
                description: attr.description,
                data_type: attr.data_type,
                validation_rules: attr.validation_rules,
                enum_values: attr.enum_values,
                input_type: attr.input_type,
                placeholder: attr.placeholder,
                help_text: attr.help_text,
                category: attr.category,
                display_order: attr.display_order,
                is_global: attr.is_global,
                attribute_metadata: attr.attribute_metadata,
            });
            setEnumValues(attr.enum_values || []);
            setMetadataJson(JSON.stringify(attr.attribute_metadata || {}, null, 2));
        } else {
            setEditingAttribute(null);
            setFormData({
                attribute_key: '',
                label: '',
                description: '',
                data_type: 'string',
                validation_rules: {},
                enum_values: [],
                input_type: '',
                placeholder: '',
                help_text: '',
                category: '',
                display_order: 0,
                is_global: false,
                attribute_metadata: {},
            });
            setEnumValues([]);
            setEnumValueInput('');
            setMetadataJson('{}');
            setMetadataError('');
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingAttribute(null);
        setEnumValues([]);
        setEnumValueInput('');
        setMetadataJson('{}');
        setMetadataError('');
    };

    const handleSave = () => {
        // Validate metadata JSON if data_type is object
        if (formData.data_type === 'object') {
            try {
                const parsed = JSON.parse(metadataJson);
                formData.attribute_metadata = parsed;
                setMetadataError('');
            } catch (e) {
                setMetadataError(t('common.invalidJson'));
                return;
            }
        } else {
            formData.attribute_metadata = {};
        }

        // Set enum_values if data_type allows it
        if (formData.data_type === 'string' && enumValues.length > 0) {
            formData.enum_values = enumValues;
        } else {
            formData.enum_values = undefined;
        }

        if (editingAttribute) {
            documentAttributeService.update(editingAttribute.document_attribute_id, {
                ...formData,
                document_attribute_id: editingAttribute.document_attribute_id,
            });
        } else {
            documentAttributeService.create(formData);
        }
        loadAttributes();
        handleCloseModal();
    };

    const handleDelete = (id: string) => {
        if (confirm(t('modal.confirmDelete'))) {
            documentAttributeService.delete(id);
            loadAttributes();
        }
    };

    const addEnumValue = () => {
        if (enumValueInput.trim()) {
            setEnumValues([...enumValues, enumValueInput.trim()]);
            setEnumValueInput('');
        }
    };

    const removeEnumValue = (index: number) => {
        setEnumValues(enumValues.filter((_, i) => i !== index));
    };

    const handleDataTypeChange = (dataType: AttributeDataType) => {
        setFormData({ ...formData, data_type: dataType });
        if (dataType !== 'string') {
            setEnumValues([]);
        }
        if (dataType !== 'object') {
            setMetadataJson('{}');
            setMetadataError('');
        }
    };

    // Calculate usage count (in how many schemas this attribute is used)
    const getUsageCount = (_attributeId: string): number => {
        if (!documentType) return 0;
        const schemas = documentSchemaService.getAll().filter((s) => s.document_type === documentType && !s.deleted_at);
        // In real implementation, we'd query schema attributes
        // For now, return a mock count
        return Math.floor(Math.random() * schemas.length) + 1;
    };

    if (isLoading || !documentType) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
                    <p className="text-tertiary">{t('loading')}</p>
                </div>
            </div>
        );
    }

    const categoryOptions = [
        { id: '', label: t('attributes.categoryPlaceholder') },
        ...categories.map((cat) => ({ id: cat!, label: cat! })),
    ];

    return (
        <div className="space-y-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm">
                <Link
                    to="/admin/documents/types"
                    className="text-tertiary hover:text-primary"
                >
                    {t('breadcrumb.documentTypes')}
                </Link>
                <span className="text-tertiary">/</span>
                <span className="text-primary">{currentDocType?.label || documentType}</span>
                <span className="text-tertiary">/</span>
                <span className="text-primary">{t('breadcrumb.attributes')}</span>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-primary">
                        {t('attributes.title')} - {currentDocType?.label || documentType}
                    </h2>
                    <p className="text-tertiary mt-1">{t('attributes.description')}</p>
                </div>
                <Button
                    onClick={() => handleOpenModal()}
                    size="md"
                    color="primary"
                    iconLeading={Plus}
                >
                    {t('attributes.newAttribute')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Input
                        placeholder={t('attributes.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('attributes.dataTypePlaceholder')}
                        items={dataTypeOptions}
                        selectedKey={dataTypeFilter}
                        onSelectionChange={(key) => setDataTypeFilter(key as AttributeDataType | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('attributes.categoryPlaceholder')}
                        items={categoryOptions}
                        selectedKey={categoryFilter}
                        onSelectionChange={(key) => setCategoryFilter(key ? String(key) : '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Attributes Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={`${t('attributes.title')} (${attributes.length})`}
                    description={t('attributes.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('attributes.headers.attributeKey')} isRowHeader />
                        <Table.Head label={t('attributes.headers.label')} />
                        <Table.Head label={t('attributes.headers.dataType')} />
                        <Table.Head label={t('attributes.headers.category')} />
                        <Table.Head label={t('attributes.headers.usageCount')} />
                        <Table.Head label={t('attributes.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {attributes.map((attr) => (
                            <Table.Row key={attr.document_attribute_id}>
                                <Table.Cell>
                                    <div className="font-medium text-primary font-mono">{attr.attribute_key}</div>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="font-medium text-primary">{attr.label}</div>
                                    {attr.description && (
                                        <div className="text-sm text-tertiary">{attr.description}</div>
                                    )}
                                </Table.Cell>
                                <Table.Cell>
                                    <Badge color="gray" size="sm">{t(`attributes.dataTypes.${attr.data_type}`)}</Badge>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{attr.category || '-'}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <span className="text-sm text-tertiary">{getUsageCount(attr.document_attribute_id)}</span>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={() => handleOpenModal(attr)}
                                            size="sm"
                                            color="secondary"
                                            iconLeading={Edit01}
                                        >
                                            {t('attributes.actions.edit')}
                                        </Button>
                                        <Button
                                            onClick={() => handleDelete(attr.document_attribute_id)}
                                            size="sm"
                                            color="secondary-destructive"
                                            iconLeading={Trash01}
                                        >
                                            {t('attributes.actions.delete')}
                                        </Button>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {attributes.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <h3 className="text-lg font-medium text-primary mb-2">{t('noData.title')}</h3>
                        <p className="text-tertiary">{t('noData.message')}</p>
                    </div>
                </div>
            )}

            {/* Modal */}
            <DialogTrigger isOpen={isModalOpen} onOpenChange={setIsModalOpen}>
                <ModalOverlay>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-4xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-h-[90vh] overflow-y-auto">
                                <h2 className="text-xl font-semibold text-primary mb-6">
                                    {editingAttribute ? t('attributes.modal.edit') : t('attributes.modal.new')}
                                </h2>

                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Input
                                            label={t('attributes.modal.fields.attributeKey')}
                                            placeholder={t('attributes.modal.fields.attributeKeyPlaceholder')}
                                            value={formData.attribute_key}
                                            onChange={(value) => setFormData({ ...formData, attribute_key: value })}
                                            isRequired
                                            size="md"
                                            isDisabled={!!editingAttribute}
                                        />

                                        <Select
                                            label={t('attributes.modal.fields.dataType')}
                                            items={dataTypeOptions.filter((opt) => opt.id !== '')}
                                            selectedKey={formData.data_type}
                                            onSelectionChange={(key) => handleDataTypeChange(key as AttributeDataType)}
                                            isRequired
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>
                                    </div>

                                    <Input
                                        label={t('attributes.modal.fields.label')}
                                        placeholder={t('attributes.modal.fields.labelPlaceholder')}
                                        value={formData.label}
                                        onChange={(value) => setFormData({ ...formData, label: value })}
                                        isRequired
                                        size="md"
                                    />

                                    <Input
                                        label={t('attributes.modal.fields.description')}
                                        placeholder={t('attributes.modal.fields.descriptionPlaceholder')}
                                        value={formData.description || ''}
                                        onChange={(value) => setFormData({ ...formData, description: value })}
                                        size="md"
                                    />

                                    {/* Enum Values - only for string type */}
                                    {formData.data_type === 'string' && (
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-primary">
                                                {t('attributes.modal.fields.enumValues')}
                                            </label>
                                            <div className="flex gap-2">
                                                <Input
                                                    placeholder={t('attributes.modal.fields.enumValuesPlaceholder')}
                                                    value={enumValueInput}
                                                    onChange={setEnumValueInput}
                                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            addEnumValue();
                                                        }
                                                    }}
                                                    size="md"
                                                />
                                                <Button onClick={addEnumValue} size="md" color="secondary">
                                                    <Plus className="size-4" />
                                                </Button>
                                            </div>
                                            {enumValues.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {enumValues.map((val, idx) => (
                                                        <Badge key={idx} color="gray" size="sm" className="flex items-center gap-1">
                                                            {val}
                                                            <button
                                                                onClick={() => removeEnumValue(idx)}
                                                                className="ml-1 hover:text-error"
                                                            >
                                                                <X className="size-3" />
                                                            </button>
                                                        </Badge>
                                                    ))}
                                                </div>
                                            )}
                                            <p className="text-xs text-tertiary">{t('attributes.modal.fields.enumValuesHelp')}</p>
                                        </div>
                                    )}

                                    {/* Metadata JSON - only for object type */}
                                    {formData.data_type === 'object' && (
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-primary">
                                                {t('attributes.modal.fields.metadata')}
                                            </label>
                                            <textarea
                                                className="w-full rounded-lg border border-secondary bg-primary px-3 py-2 text-sm text-primary placeholder-tertiary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                                                rows={6}
                                                value={metadataJson}
                                                onChange={(e) => {
                                                    setMetadataJson(e.target.value);
                                                    setMetadataError('');
                                                }}
                                                placeholder={t('attributes.modal.fields.metadataPlaceholder')}
                                            />
                                            {metadataError && (
                                                <p className="text-xs text-error">{metadataError}</p>
                                            )}
                                            <p className="text-xs text-tertiary">{t('attributes.modal.fields.metadataHelp')}</p>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Input
                                            label={t('attributes.modal.fields.inputType')}
                                            placeholder={t('attributes.modal.fields.inputTypePlaceholder')}
                                            value={formData.input_type || ''}
                                            onChange={(value) => setFormData({ ...formData, input_type: value })}
                                            size="md"
                                        />

                                        <Input
                                            label={t('attributes.modal.fields.placeholder')}
                                            placeholder={t('attributes.modal.fields.placeholderPlaceholder')}
                                            value={formData.placeholder || ''}
                                            onChange={(value) => setFormData({ ...formData, placeholder: value })}
                                            size="md"
                                        />
                                    </div>

                                    <Input
                                        label={t('attributes.modal.fields.helpText')}
                                        placeholder={t('attributes.modal.fields.helpTextPlaceholder')}
                                        value={formData.help_text || ''}
                                        onChange={(value) => setFormData({ ...formData, help_text: value })}
                                        size="md"
                                    />

                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Input
                                            label={t('attributes.modal.fields.category')}
                                            placeholder={t('attributes.modal.fields.categoryPlaceholder')}
                                            value={formData.category || ''}
                                            onChange={(value) => setFormData({ ...formData, category: value })}
                                            size="md"
                                        />

                                        <Input
                                            label={t('attributes.modal.fields.displayOrder')}
                                            value={String(formData.display_order || 0)}
                                            onChange={(value) => setFormData({ ...formData, display_order: Number(value) || 0 })}
                                            size="md"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-4 mt-6">
                                    <Button onClick={handleCloseModal} size="md" color="secondary">
                                        {t('modal.cancel')}
                                    </Button>
                                    <Button onClick={handleSave} size="md" color="primary">
                                        {editingAttribute ? t('modal.save') : t('modal.create')}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            </DialogTrigger>
        </div>
    );
};
